import { AVAILABILITY_ERROR_CODES } from '@shared/core/availabilities'
import { BOOKING_ERROR_CODES } from '@shared/core/bookings'
import { GOOGLE_LOOKUP_ERROR_CODES } from '@shared/core/google'
import { useCallback } from 'react'
import { FORBIDDEN_ERROR, NO_SIGN_UP_ERROR, TOKEN_ERROR, TOO_MANY_REQUESTS_ERROR } from '../constants/errorCodes'

type UseHandleRetryProps = {
  numberOfRetries?: number
  errorCodes?: number[]
  errorCodeNames?: string[]
}

// Matched on the body's `code`, not the status: a throttled Google lookup shares 503 with a
// retry-worthy Paddle, and retrying it re-enters the auth guard to burn more Google quota.
// A refused booking calendar (409) is permanent, so retries only delay the "contact the business" state.
// A 429 retried would only feed the limit that refused it.
const DEFAULT_PARAMS = {
  numberOfRetries: 2,
  errorCodes: [TOKEN_ERROR, FORBIDDEN_ERROR, NO_SIGN_UP_ERROR, TOO_MANY_REQUESTS_ERROR],
  errorCodeNames: [
    GOOGLE_LOOKUP_ERROR_CODES.UNAVAILABLE,
    AVAILABILITY_ERROR_CODES.CALENDAR_REFUSED,
    BOOKING_ERROR_CODES.tooManyRequests,
  ],
}

export const useHandleRetry = ({
  numberOfRetries = DEFAULT_PARAMS.numberOfRetries,
  errorCodes = DEFAULT_PARAMS.errorCodes,
  errorCodeNames = DEFAULT_PARAMS.errorCodeNames,
}: UseHandleRetryProps = DEFAULT_PARAMS) => {
  return useCallback(
    (count, error) =>
      count <= numberOfRetries &&
      !errorCodes.includes(error?.response?.status) &&
      !errorCodeNames.includes(error?.response?.data?.code),
    [numberOfRetries, errorCodes, errorCodeNames],
  )
}
